import type { Annotation, Canvas, Storyboard, SubCanvas } from "@/utils/types";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { AnyAction, Dispatch } from "@reduxjs/toolkit";
import type { Database, Json } from "@/utils/supabase.types";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useIssuesStore } from "./zustand/issues-store";

// Type aliases from Database schema
type ElementMetadata = Database["public"]["Tables"]["element_metadata"]["Row"];
type SubCanvasRow = Database["public"]["Tables"]["sub_canvases"]["Row"];
type ProjectRow = Database["public"]["Tables"]["projects"]["Row"];
type ActivityStreamDiff =
  Database["public"]["Tables"]["activity_stream_diffs"]["Row"];
type ProjectLoadingProgress =
  Database["public"]["Tables"]["project_loading_progress"]["Row"];

// Helper type for Json arrays that we know are arrays
type JsonArray = unknown[];

// Helper function to check if Json is an array
const isJsonArray = (value: unknown): value is JsonArray => {
  return Array.isArray(value);
};

// Helper function to get array length safely
const getArrayLength = (value: unknown): number => {
  return isJsonArray(value) ? value.length : 0;
};

// State interface
interface SupabaseState {
  // Diff's are looked up based on the activity ID (equal to ID)
  activityStreamDiffs: Record<string, ActivityStreamDiff>;
  activityStreamDiffsLoadStatus: "idle" | "loading" | "succeeded" | "failed";

  // Project loading statuses
  projectLoadingProgress: ProjectLoadingProgress | null;
  projectLoadingProgressLoadStatus: "idle" | "loading" | "succeeded" | "failed";

  projectIdsByLastUpdated: Array<{ id: string; most_recent_update: string }>;
  projectIdsByLastUpdatedLoadStatus:
    | "idle"
    | "loading"
    | "succeeded"
    | "failed";
  projects: Record<string, ProjectRow>;
  projectsLoadStatus: "idle" | "loading" | "succeeded" | "failed";

  ignoreCanvasUpdates: boolean;

  subCanvases: Record<string, SubCanvasRow>;
  subCanvasesLoadStatus: "idle" | "loading" | "succeeded" | "failed";

  elementMetadata: Record<string, ElementMetadata>;
  elementMetadataLoadStatus: "idle" | "loading" | "succeeded" | "failed";

  // attached figma context by canvas id
  figmaContext: Record<string, unknown>;
  dsPrompt: string;
}

// Action payload interfaces
interface DSPromptPayload {
  prompt?: string;
}

interface UpdateCanvasFieldsPayload {
  id: string;
  fields: Record<string, unknown>;
}

interface SnapshotPayload<T> {
  newItems?: T[];
  deletedItems?: T[];
  forceUpdate?: boolean;
}

const initialState: SupabaseState = {
  // Diff's are looked up based on the activity ID (equal to ID)
  activityStreamDiffs: {},
  activityStreamDiffsLoadStatus: "idle",

  // Project loading statuses
  projectLoadingProgress: null,
  projectLoadingProgressLoadStatus: "idle",

  projectIdsByLastUpdated: [],
  projectIdsByLastUpdatedLoadStatus: "idle",
  projects: {},
  projectsLoadStatus: "idle",

  ignoreCanvasUpdates: false,

  subCanvases: {},
  subCanvasesLoadStatus: "idle",

  elementMetadata: {},
  elementMetadataLoadStatus: "idle",

  // attached figma context by canvas id
  figmaContext: {},
  dsPrompt: "",
};

export const supabaseSlice = createSlice({
  name: "supabaseStore",
  initialState,

  reducers: {
    setDSPrompt: (state, action: PayloadAction<DSPromptPayload>) => {
      if (action.payload?.prompt) {
        state.dsPrompt = action.payload.prompt;
      } else {
        state.dsPrompt = "";
      }
    },
    onDSPromptSnapshot: (state, action: PayloadAction<string>) => {
      state.dsPrompt = "";
      state.dsPrompt = action.payload || "";
    },
    setFigmaContext: (
      state,
      action: PayloadAction<Record<string, unknown> | null>
    ) => {
      state.figmaContext = action.payload || {};
    },
    onFigmaContextSnapshot: (
      state,
      action: PayloadAction<Record<string, unknown> | null>
    ) => {
      state.figmaContext = {};
      state.figmaContext = action.payload || {};
    },
    setElementMetadata: (state, action: PayloadAction<ElementMetadata[]>) => {
      state.elementMetadata = {};

      if (action.payload) {
        for (const metadata of action.payload) {
          (state.elementMetadata as Record<string, ElementMetadata>)[
            metadata.hash
          ] = metadata;
        }
      }
      state.elementMetadataLoadStatus = "succeeded";
    },
    onElementMetadataSnapshot: (
      state,
      action: PayloadAction<SnapshotPayload<ElementMetadata>>
    ) => {
      if (action.payload?.newItems) {
        for (const metadata of action.payload.newItems) {
          (state.elementMetadata as Record<string, ElementMetadata>)[
            metadata.hash
          ] = metadata;
        }
      }

      if (action.payload?.deletedItems) {
        for (const metadata of action.payload.deletedItems) {
          delete state.elementMetadata[metadata.hash];
        }
      }
    },
    setElementMetadataLoading: (state) => {
      state.elementMetadataLoadStatus = "loading";
    },
    setIgnoreCanvasUpdates: (state, action: PayloadAction<boolean>) => {
      state.ignoreCanvasUpdates = action.payload;
    },
    setSubCanvases: (state, action: PayloadAction<SubCanvasRow[]>) => {
      state.subCanvases = {};

      if (action.payload) {
        for (const canvas of action.payload) {
          if (
            // @ts-expect-error - storyboards is a Json array
            getArrayLength(state.subCanvases?.[canvas.id]?.storyboards) > 0 &&
            getArrayLength(canvas?.storyboards) === 0
          ) {
            canvas.storyboards = state.subCanvases[canvas.id].storyboards;
          }

          if (
            getArrayLength(state.subCanvases?.[canvas.id]?.annotations) > 0 &&
            getArrayLength(canvas?.annotations) === 0
          ) {
            canvas.annotations = state.subCanvases[canvas.id].annotations;
          }

          state.subCanvases[canvas.id] = canvas;
        }
      }
      state.subCanvasesLoadStatus = "succeeded";
    },
    onSubCanvasesSnapshot: (
      state,
      action: PayloadAction<SnapshotPayload<SubCanvasRow>>
    ) => {
      if (state.ignoreCanvasUpdates && !action.payload?.forceUpdate) {
        return;
      }

      if (action.payload?.newItems) {
        for (const subcanvas of action.payload.newItems) {
          const lastUpdated = state.subCanvases[subcanvas.id]?.last_updated;
          if (
            lastUpdated &&
            new Date(lastUpdated) > new Date(subcanvas.last_updated)
          ) {
            continue;
          }

          // Do not delete the storyboards as it can sometimes get too big and not come back
          // in the snapshots
          if (
            getArrayLength(state.subCanvases?.[subcanvas.id]?.storyboards) >
              0 &&
            getArrayLength(subcanvas?.storyboards) === 0
          ) {
            subcanvas.storyboards = state.subCanvases[subcanvas.id].storyboards;
          }

          // Do not delete the annotations as it can sometimes get too big and not come back
          // in the snapshots
          if (
            getArrayLength(state.subCanvases?.[subcanvas.id]?.annotations) >
              0 &&
            getArrayLength(subcanvas?.annotations) === 0
          ) {
            subcanvas.annotations = state.subCanvases[subcanvas.id].annotations;
          }

          state.subCanvases[subcanvas.id] = subcanvas;
        }
      }

      if (action.payload?.deletedItems) {
        for (const subcanvas of action.payload.deletedItems) {
          delete state.subCanvases[subcanvas.id];
        }
      }
    },
    setSubCanvasesLoading: (state) => {
      state.subCanvasesLoadStatus = "loading";
    },
    setProjectIdsByLastUpdated: (
      state,
      action: PayloadAction<Array<{ id: string; most_recent_update: string }>>
    ) => {
      state.projectIdsByLastUpdated = action.payload;
      state.projectIdsByLastUpdatedLoadStatus = "succeeded";
    },
    setProjectIdsByLastUpdatedLoading: (state) => {
      state.projectIdsByLastUpdatedLoadStatus = "loading";
    },
    setProjects: (state, action: PayloadAction<ProjectRow[]>) => {
      state.projects = {};
      if (action.payload) {
        for (const project of action.payload) {
          state.projects[project.id] = project;
        }
      }
      state.projectsLoadStatus = "succeeded";
    },
    onProjectsSnapshot: (
      state,
      action: PayloadAction<SnapshotPayload<ProjectRow>>
    ) => {
      if (action.payload?.newItems) {
        for (const project of action.payload.newItems) {
          state.projects[project.id] = project;
        }
      }

      if (action.payload?.deletedItems) {
        for (const project of action.payload.deletedItems) {
          delete state.projects[project.id];
        }
      }
    },
    setProjectsLoading: (state) => {
      state.projectsLoadStatus = "loading";
    },

    setActivityStreamDiffs: (
      state,
      action: PayloadAction<ActivityStreamDiff[]>
    ) => {
      // we don't reset activityStreamDiffs to {} because they are immutable - new ones are just appended to the state
      if (action.payload) {
        for (const diff of action.payload) {
          state.activityStreamDiffs[diff.id] = diff;
        }
      }
      state.activityStreamDiffsLoadStatus = "succeeded";
    },
    onActivityStreamDiffsSnapshot: (
      state,
      action: PayloadAction<SnapshotPayload<ActivityStreamDiff>>
    ) => {
      if (action.payload?.newItems) {
        for (const diff of action.payload.newItems) {
          state.activityStreamDiffs[diff.id] = diff;
        }
      }

      if (action.payload?.deletedItems) {
        for (const diff of action.payload.deletedItems) {
          delete state.activityStreamDiffs[diff.id];
        }
      }
    },
    setActivityStreamDiffsLoading: (state) => {
      state.activityStreamDiffsLoadStatus = "loading";
    },

    setProjectLoadingProgress: (
      state,
      action: PayloadAction<ProjectLoadingProgress>
    ) => {
      state.projectLoadingProgress = action.payload;
      state.projectLoadingProgressLoadStatus = "succeeded";
    },
    setProjectLoadingProgressLoading: (state) => {
      state.projectLoadingProgressLoadStatus = "loading";
    },
    onProjectLoadingProgressSnapshot: (
      state,
      action: PayloadAction<ProjectLoadingProgress>
    ) => {
      state.projectLoadingProgress = action.payload;
    },
  },
});

/**
 * Utility to update storyboards & annotations & set in redux instantly
 */
export const updateCanvasStoryboards = async (
  supabaseClient: SupabaseClient<Database> | undefined,
  dispatch: Dispatch<AnyAction>,
  newStoryboards: Storyboard[],
  canvas: Canvas | SubCanvas,
  updateTs?: Date,
  annotations?: Annotation[]
) => {
  if (!supabaseClient) {
    console.error("Supabase client not available");
    return;
  }
  // Don't allow updating to 0 storyboards
  if (!newStoryboards?.length) {
    return;
  }

  const last_updated = updateTs || new Date();

  const updateDict: Record<string, unknown> = {
    storyboards: newStoryboards,
    last_updated: last_updated.toISOString(),
  };

  if (annotations) {
    updateDict.annotations = annotations;
  }

  const newItem = {
    ...canvas,
    ...updateDict,
  };

  // This is a sub-canvas
  if ((canvas as SubCanvas).canvas_id) {
    dispatch(
      onSubCanvasesSnapshot({
        newItems: [newItem as SubCanvasRow],
        forceUpdate: true,
      })
    );

    await supabaseClient
      .from("sub_canvases")
      .update(updateDict)
      .eq("id", canvas.id);

    return;
  }

  await useIssuesStore
    .getState()
    .updateCanvas(supabaseClient, canvas.id, newItem as Canvas);
};

export const {
  setElementMetadata,
  onElementMetadataSnapshot,
  setElementMetadataLoading,
  setProjects,
  onProjectsSnapshot,
  setProjectsLoading,
  setSubCanvasesLoading,
  setSubCanvases,
  onSubCanvasesSnapshot,
  setIgnoreCanvasUpdates,
  setActivityStreamDiffs,
  setActivityStreamDiffsLoading,
  onActivityStreamDiffsSnapshot,
  setProjectLoadingProgress,
  onProjectLoadingProgressSnapshot,
  setProjectLoadingProgressLoading,

  setProjectIdsByLastUpdated,
  setProjectIdsByLastUpdatedLoading,
  setFigmaContext,
  onFigmaContextSnapshot,

  setDSPrompt,
  onDSPromptSnapshot,
} = supabaseSlice.actions;

export default supabaseSlice.reducer;
